<template>
  <div class="layout layout-column">

    <!-- Warnung, wenn mehr als ein Histologieeintrag aus älterer Version existiert -->

    <lx-alert-box v-if="model && model.length > 1 && formStatus !== 'sent'"
                  color="red"
                  class="layout wrap flex-gt-sm-100 flex-100 px-2 mt-3 mb-2 mx-2">
      <span>{{ $t('FORMS_DIVERSE.HISTOLOGY_WARNING_TOO_MANY_ITEMS') }}</span>
    </lx-alert-box>

    <div class="layout-row">
      <validation-provider tag="div"
                           :name="path"
                           :ref="path">
        <v-text-field v-model.lazy="model"
                      id="histologie"
                      v-show="false"
        ></v-text-field>
      </validation-provider>
    </div>

    <div v-bind:style="{'z-index': '4'}" class="layout-align-start-center pa-2" v-if="!model || model.length === 0">
      <lx-btn :disabled="isDisabled"
              icon="add_circle"
              :color="compColor.primeColor"
              :iconColor="'white'"
              :tooltip="$t('FORMS_DIVERSE.ADD_NEW_HISTOLOGY')"
              :label="$t('FORMS_DIVERSE.ADD_NEW_HISTOLOGY')"
              iconSize="21"
              :width="'auto'"
              :height="35"
              classes="text-none"
              @click="addNewHistology"
      ></lx-btn>
    </div>

    <div :key="index" v-for="(item, index) in model"
         class="layout-row pa-0"
         :class="{'elevation-5': compColor.isInDarkMode && model.length > 1, 'grey lighten-3': !compColor.isInDarkMode && model.length > 1, 'ma-2': model.length > 1, 'my-2': model.length === 1}"
         style="border-radius:4px;"
         :style="{'border': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">
      <div class="layout-row layout-wrap flex pa-4">

        <!-- Einsendenr -->

        <div class="flex-gt-sm-100 flex-100 px-2">
          <validation-provider tag="div"
                               :name="path + '.' + index + '.histologie_einsende_nr'"
                               :ref="path + '.' + index + '.histologie_einsende_nr'"
                               rules="min:2|trim_string"
                               v-slot="{ errors }">
            <long-input
              :color="compColor.primeColor"
              :name="path + '.' + index + '.histologie_einsende_nr'"
              :label="$t('FORMS_DIVERSE.HISTOLOGY_SEND_NUMBER')"
              v-model="item.histologie_einsende_nr"
              :disabled="isDisabled"
              :dense="denseFields"
              :error="errors[0]"
            ></long-input>
          </validation-provider>
        </div>

        <!-- Histologiedatum -->

        <div class="flex-gt-sm-50 flex-100 px-2 mb-4">
          <validation-provider tag="div"
                               :name="path + '.' + index + '.tumor_histologiedatum'"
                               :ref="path + '.' + index + '.tumor_histologiedatum'"
                               :rules="{required:checkIfDateIsRequired(item),german_date:true,date_not_in_future:true,date_must_be_lesser_than_death:[deathDate, item.tumor_histologiedatum_genauigkeit]}"
                               v-slot="{ errors }">
            <lx-datepicker
              :color="compColor.textInput"
              :name="path + '.' + index + '.tumor_histologiedatum'"
              :label="formatRequiredLabel(index,'FORMS_DIVERSE.HISTOLOGY_DATE')"
              class="mr-1"
              :disabled="isDisabled"
              @input="setDateAccuracyAfterDate(index)"
              :hide-details="!errors || errors.length <= 0"
              :dense="denseFields"
              :error="errors[0]"
              v-model="item.tumor_histologiedatum"
            ></lx-datepicker>
          </validation-provider>
        </div>

        <!-- Datumsgenauigkeit Histologiedatum -->

        <div class="flex-gt-sm-50 flex-100 px-2 layout-row">
          <validation-provider tag="div"
                               class="flex"
                               :name="path + '.' + index + '.tumor_histologiedatum_genauigkeit'"
                               :ref="path + '.' + index + '.tumor_histologiedatum_genauigkeit'"
                               :rules="{required: Boolean(model[index].tumor_histologiedatum)}"
                               v-slot="{ errors }">
            <v-select
              :color="compColor.primeColor"
              :label="formatRequiredLabel(index,'FORMS_DIVERSE.HISTOLOGY_DATE_ACCURACY')"
              v-model="item.tumor_histologiedatum_genauigkeit"
              :items="DateAccuracyTypes"
              :disabled="isDisabled || !model[index].tumor_histologiedatum"
              outlined
              clearable
              @change="validateDateAfterChangeOfAccuracy(index)"
              :hide-details="!errors || errors.length <= 0"
              :dense="denseFields"
              :error-messages="errors[0]"
              :menu-props="{ bottom: true, offsetY: true }"
              :dark="compColor.isInDarkMode">
              <template v-slot:item="{ item, on, attrs }">
                <v-list-item v-on="on"
                             :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                  <v-list-item-content class="py-0">
                    <label style="cursor:pointer;"
                           :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                           class="lighten-4 text--darken-2">{{ item.text }}</label>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-select>
          </validation-provider>

          <v-tooltip bottom open-delay="1000">
            <template v-slot:activator="{ on }">
              <div v-on="on" class="blue--text text--lighten-1 layout-row layout-align-start-start pt-2 ml-3"
                   :class="{'mt-1': !denseFields}"
                   style="cursor: pointer;">
                <v-icon :color="compColor.prime" medium>info</v-icon>
              </div>
            </template>
            <p style="max-width: 400px;" class="my-1">{{ $t('FORMS_DIVERSE.TOOLTIP_DATE_ACCURACY') }}</p>
          </v-tooltip>
        </div>

        <!-- Morphologie Codes -->

        <div class="flex-100 px-2 mb-4 mt-2">
          <fake-input-frame :label="$t('FORMS_DIVERSE.HISTOLOGY_MORPHOLOGY_CODES')"
                            :disabled="isDisabled">
            <div class="layout-column flex px-2 pb-2">
              <div v-bind:style="{'z-index': '4'}" class="layout-row flex layout-align-start-center pa-1">
                <lx-btn :disabled="isDisabled || (item.morphologie_icd_o && item.morphologie_icd_o.length === 5)"
                        icon="add_circle"
                        :color="compColor.primeColor"
                        :iconColor="'white'"
                        :label="$t('FORMS_DIVERSE.HISTOLOGY_ADD_MORPHOLOGY_CODE')"
                        :tooltip="$t('FORMS_DIVERSE.HISTOLOGY_ADD_MORPHOLOGY_CODE')"
                        iconSize="21"
                        :width="'auto'"
                        :height="35"
                        @click="addNewMorphologyCode(index)"
                ></lx-btn>
              </div>

              <div :key="codeIndex" v-for="(code, codeIndex) in item.morphologie_icd_o"
                   class="layout-row pa-0 ma-2"
                   :class="{'elevation-5': compColor.isInDarkMode, 'grey lighten-3': !compColor.isInDarkMode}"
                   style="border-radius:4px;"
                   :style="{'border': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">

                <div class="layout-column flex px-3">
                  <div class="layout-row layout-wrap flex-100">

                    <!-- Morphologie-Code  -->

                    <div class="flex-gt-sm-20 flex-100  px-2 py-3">
                      <validation-provider tag="div"
                                           :name="path + '.' + index + '.morphologie_icd_o.' + codeIndex + '.code'"
                                           :ref="path + '.' + index + '.morphologie_icd_o.' + codeIndex + '.code'"
                                           rules="required|min:2|morphology_code_auto_complete"
                                           v-slot="{ errors }">
                        <lx-autocomplete
                          :label="$t('FORMS_DIVERSE.HISTOLOGY_MORPHOLOGY_CODE') + ' *'"
                          v-model="code.code"
                          :error="errors[0]"
                          :hideDetails="!errors || errors.length <= 0"
                          :index="codeIndex"
                          :parentIndex="index"
                          @search-method="getDataForAutocomplete"
                          @input="setMorphology"
                          :disabled="isDisabled"
                          :dense="denseFields"
                          :results="resultSearchModel">
                          <template v-slot="slotProps">
                            <div class="layout-row">
                              <div style="width: 100px;border-radius: 4px;"
                                   class="mr-2 px-2 white lighten-2 layout-column layout-align-center-center">
                                <h3>
                                  <lx-text-highlight :text="slotProps.lineItem.code"
                                                     :search-string="slotProps.searchText"
                                                     :color="compColor.prime"></lx-text-highlight>
                                </h3>
                              </div>
                              <div class="flex">
                                <span class="body-2">
                                  <lx-text-highlight :text="slotProps.lineItem.description_long" :search-string="slotProps.searchText"
                                                     :color="compColor.prime"></lx-text-highlight>
                                </span>
                              </div>
                            </div>
                          </template>
                        </lx-autocomplete>
                      </validation-provider>
                    </div>

                    <!-- Morphologie ICDO-Version -->

                    <div class="flex-gt-sm-35 flex-100 px-2 py-3">
                      <validation-provider tag="div"
                                           :name="path + '.' + index + '.morphologie_icd_o.' + codeIndex + '.version'"
                                           :ref="path + '.' + index + '.morphologie_icd_o.' + codeIndex + '.version'"
                                           rules="required"
                                           v-slot="{ errors }">
                        <v-select
                          :color="compColor.primeColor"
                          :label="$t('FORMS_DIVERSE.HISTOLOGY_MORPHOLOGY_ICDO_VERSION') + ' *'"
                          v-model="code.version"
                          :items="icdOVersions"
                          :disabled="isDisabled"
                          outlined
                          class="mb-0"
                          @change="resetMorphology(index, codeIndex)"
                          :persistent-hint="true"
                          :hint="$t('FORMS_DIVERSE.CODE_VERSION_HINT')"
                          :dense="denseFields"
                          :error-messages="errors[0]"
                          :menu-props="{ bottom: true, offsetY: true }"
                          :dark="compColor.isInDarkMode">
                          <template v-slot:item="{ item, on, attrs }">
                            <v-list-item v-on="on"
                                         :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                              <v-list-item-content class="py-0">
                                <label style="cursor:pointer;"
                                       :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                       class="lighten-4 text--darken-2">{{ item.text }}</label>
                              </v-list-item-content>
                            </v-list-item>
                          </template>
                        </v-select>
                      </validation-provider>
                    </div>

                    <!-- Morphologie Freitext -->

                    <div class="flex-gt-sm-45 flex-100 px-2 py-3">
                      <validation-provider tag="div"
                                           :name="path + '.' + index + '.morphologie_icd_o.' + codeIndex + '.description'"
                                           :ref="path + '.' + index + '.morphologie_icd_o.' + codeIndex + '.description'"
                                           rules="">
                        <v-text-field
                          :label="$t('FORMS_DIVERSE.HISTOLOGY_MORPHOLOGY_ICDO_DESCRIPTION')"
                          :color="compColor.primeColor"
                          :dark="compColor.isInDarkMode"
                          v-model="code.description"
                          hide-details
                          disabled
                          class="mb-0"
                          :dense="denseFields"
                          outlined
                        ></v-text-field>
                      </validation-provider>
                    </div>
                  </div>

                  <div class="layout-wrap flex-gt-sm-100 flex-100 mb-4 mr-2"
                       v-if="code.code && !checkIfCodeHasCorrectSuffix(code.code) && validationRef.errors  && validationRef.errors[path + '.' + index + '.morphologie_icd_o.' + codeIndex + '.code'] && validationRef.errors[path + '.' + index + '.morphologie_icd_o.' + codeIndex + '.code'].length === 0">
                    <!-- Warnung wenn Morphologie-Code nicht auf 2 oder 3 endet-->
                    <lx-alert-box color="orange">
                      {{ $t('COMPONENT_DOCUMENTATION.WARNING_MORPHOLOGY_CODE', {dignity: code.code.substr(-1)}) }}
                    </lx-alert-box>
                  </div>

                </div>

                <div class="layout-column layout-align-center-center px-3"
                     :class="{'grey lighten-2': !compColor.isInDarkMode}"
                     :style="{'border-left': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">
                  <lx-btn :color="compColor.primeColor"
                          :iconColor="'white'"
                          iconSize="21"
                          :width="35"
                          :height="35"
                          :disabled="isDisabled"
                          @click="deleteItem(path + '.' + index + '.morphologie_icd_o', codeIndex, path + '.' + index + '.morphologie_icd_o.' + codeIndex + '.code')"
                          icon="delete_forever"></lx-btn>
                </div>
              </div>
            </div>
          </fake-input-frame>
        </div>

        <!-- Morphologie Freitext -->

        <div class="flex-gt-sm-100 flex-100 px-2 mb-6">
          <validation-provider tag="div"
                               :name="path + '.' + index + '.morphologie_freitext'"
                               :ref="path + '.' + index + '.morphologie_freitext'"
                               rules="trim_string"
                               v-slot="{ errors }">
            <long-input
              :label="$t('FORMS_DIVERSE.HISTOLOGY_MORPHOLOGY_TEXT')"
              :name="path + '.' + index + '.morphologie_freitext'"
              :color="compColor.primeColor"
              :dark="compColor.isInDarkMode"
              v-model="item.morphologie_freitext"
              :dense="denseFields"
              :hideDetails="!errors || errors.length <= 0"
              :disabled="isDisabled"
              outlined
              :error="errors[0]"
            ></long-input>
          </validation-provider>
        </div>

        <!-- Grading -->

        <div class="layout-row flex-gt-sm-100 flex-100">
          <div class="flex-gt-sm-33 flex-100 px-2">
            <validation-provider tag="div"
                                 :name="path + '.' + index + '.grading'"
                                 :ref="path + '.' + index + '.grading'"
                                 :rules="{required: Boolean(model[index].tumor_histologiedatum)}"
                                 v-slot="{ errors }">
              <v-select
                :color="compColor.primeColor"
                :label="formatRequiredLabel(index,'FORMS_DIVERSE.GRADING')"
                v-model="item.grading"
                :items="GradingTypes"
                outlined
                clearable
                :disabled="isDisabled"
                :dense="denseFields"
                :hide-details="!errors || errors.length <= 0"
                :error-messages="errors[0]"
                :menu-props="{ bottom: true, offsetY: true }"
                :dark="compColor.isInDarkMode">
                <template v-slot:item="{ item, on, attrs }">
                  <v-list-item v-on="on"
                               :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                    <v-list-item-content class="py-0">
                      <label style="cursor:pointer;"
                             :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                             class="lighten-4 text--darken-2">{{ item.text }}</label>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-select>
            </validation-provider>
          </div>

          <v-tooltip bottom open-delay="1000" max-width="800" v-if="!isDisabled">
            <template v-slot:activator="{ on }">
              <div v-on="on" class="layout-row layout-align-start-start mr-2" :class="{'mt-2': denseFields, 'mt-3': !denseFields}"
                   style="cursor: pointer;">
                <v-icon :color="compColor.prime" medium>info</v-icon>
              </div>
            </template>

            <p style="text-align: justify;" class="my-1">{{ $t('FORMS_DIVERSE.HISTOLOGY_GRADING_INFO_TEXT') }}</p>
          </v-tooltip>
        </div>

        <!--   Lymphknoten   -->

        <div class="flex-100 px-3 py-1 mb-6 mt-4" :class="isDarkMode ? 'blue-grey darken-3': compColor.prime + ' lighten-5'"
             style="border-radius: 4px;">
          <span class="body-2 font-weight-bold" :style="{color: compColor.primeColor}">Falls Lymphknoten untersucht wurden:</span>
        </div>

        <div class="flex-gt-sm-50 flex-100 px-2">
          <validation-provider tag="div"
                               :name="path + '.' + index + '.lk_untersucht'"
                               :ref="path + '.' + index + '.lk_untersucht'"
                               :rules="{integer:true, between: [0,99], nodes_must_be_greater_than: [item.lk_befallen]}"
                               v-slot="{ errors }">
            <lx-numberfield :disabled="isDisabled"
                            :dense="denseFields"
                            v-model="item.lk_untersucht"
                            :label="$t('FORMS_DIVERSE.HISTOLOGY_COUNT_EXAMINED_LYMPH_NODES')"
                            separator=","
                            @input="refactorEmptyIntValueInArray(item.lk_untersucht, index, 'lk_untersucht')"
                            :hint="$t('FORMS_DIVERSE.HISTOLOGY_HINT_LYMPH_NODES')"
                            persistent-hint
                            :error-messages="errors[0]"
            ></lx-numberfield>
          </validation-provider>
        </div>

        <div class="flex-gt-sm-50 flex-100 px-2">
          <validation-provider tag="div"
                               :name="path + '.' + index + '.lk_befallen'"
                               :ref="path + '.' + index + '.lk_befallen'"
                               :rules="{integer:true, between: [0,99], nodes_must_be_lesser_than: [item.lk_untersucht]}"
                               v-slot="{ errors }">
            <lx-numberfield :disabled="isDisabled"
                            :dense="denseFields"
                            v-model="item.lk_befallen"
                            :label="$t('FORMS_DIVERSE.HISTOLOGY_COUNT_INFECTED_LYMPH_NODES')"
                            separator=","
                            @input="refactorEmptyIntValueInArray(item.lk_befallen, index, 'lk_befallen')"
                            :hint="$t('FORMS_DIVERSE.HISTOLOGY_HINT_LYMPH_NODES')"
                            persistent-hint
                            :error-messages="errors[0]"
            ></lx-numberfield>
          </validation-provider>
        </div>

        <!--   Sentinelknoten   -->

        <div class="flex-gt-sm-50 flex-100 px-2">
          <validation-provider tag="div"
                               :name="path + '.' + index + '.sentinel_lk_untersucht'"
                               :ref="path + '.' + index + '.sentinel_lk_untersucht'"
                               :rules="{integer:true, between: [0,99], nodes_must_be_greater_than: [item.sentinel_lk_befallen]}"
                               v-slot="{ errors }">
            <lx-numberfield :disabled="isDisabled"
                            :dense="denseFields"
                            v-model="item.sentinel_lk_untersucht"
                            :label="$t('FORMS_DIVERSE.HISTOLOGY_COUNT_EXAMINED_SENTINEL_NODES')"
                            separator=","
                            :hide-details="!errors || errors.length <= 0"
                            @input="refactorEmptyIntValueInArray(item.sentinel_lk_untersucht, index, 'sentinel_lk_untersucht')"
                            :hint="$t('FORMS_DIVERSE.HISTOLOGY_HINT_LYMPH_NODES')"
                            persistent-hint
                            :error-messages="errors[0]"
            ></lx-numberfield>
          </validation-provider>
        </div>

        <div class="flex-gt-sm-50 flex-100 px-2">
          <validation-provider tag="div"
                               :name="path + '.' + index + '.sentinel_lk_befallen'"
                               :ref="path + '.' + index + '.sentinel_lk_befallen'"
                               :rules="{integer:true, between: [0,99], nodes_must_be_lesser_than: [item.sentinel_lk_untersucht]}"
                               v-slot="{ errors }">
            <lx-numberfield :disabled="isDisabled"
                            :dense="denseFields"
                            v-model="item.sentinel_lk_befallen"
                            :label="$t('FORMS_DIVERSE.HISTOLOGY_COUNT_INFECTED_SENTINEL_NODES')"
                            separator=","
                            :hide-details="!errors || errors.length <= 0"
                            @input="refactorEmptyIntValueInArray(item.sentinel_lk_befallen, index, 'sentinel_lk_befallen')"
                            :hint="$t('FORMS_DIVERSE.HISTOLOGY_HINT_LYMPH_NODES')"
                            persistent-hint
                            :error-messages="errors[0]"
            ></lx-numberfield>
          </validation-provider>
        </div>
      </div>

      <div v-if="model && model.length > 1"
           class="layout-column layout-align-center-center px-3"
           :class="{'grey lighten-2': !compColor.isInDarkMode}"
           :style="{'border-left': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">
        <lx-btn @click="deleteItem(path, index, path + '.' + index + '.sentinel_lk_befallen')"
                :color="compColor.primeColor"
                :iconColor="'white'"
                iconSize="21"
                :width="35"
                :height="35"
                :disabled="isDisabled" icon="delete_forever"></lx-btn>
      </div>
    </div>
  </div>
</template>

<script>
import metaData from '../shared/formsMetaData'
import utils from '../../../common/utils'
import LxAutocomplete from '../../lx_auto_complete/LxAutocomplete'

export default {
  name: 'HistologieMulti',
  mixins: [metaData, utils],
  components: {
    LxAutocomplete
  },
  props: {
    value: {
      type: Array,
      default: () => ([])
    },
    path: {
      type: String
    },
    deleteSelectedItem: {
      type: Function
    },
    isDisabled: {
      type: Boolean
    },
    diagnosis: {
      type: String
    },
    leadDate: {
      type: [Object, String]
    },
    validationRef: {
      type: Object,
      default: () => ({})
    },
    formStatus: {
      type: String
    },
    patientId: {
      type: String
    }
  },
  data () {
    return {
      model: [],
      icdOVersions: null,
      icdOVersionsFromLib: {},
      GradingTypes: null,
      DateAccuracyTypes: null,
      selectedIndex: null,
      resultSearchModel: {}
    }
  },
  created () {
    this.icdOVersions = this.filterVersions('icdO')
    this.icdOVersionsFromLib = this.formsMetaData.Morphologie_ICD_O_VersionTypes_from_lib
    this.GradingTypes = this.formsMetaData.GradingTypes
    this.DateAccuracyTypes = this.formsMetaData.DateAccuracyLong
  },
  methods: {
    /**
     * Check, if a field in selected histology is filled to set the date on required
     *
     * @param histology The selected histology-entry
     * @returns {boolean}
     */
    checkIfDateIsRequired (histology) {
      return Boolean(histology.grading ||
          histology.histologie_einsende_nr ||
          histology.morphologie_freitext ||
          histology.tumor_histologiedatum_genauigkeit ||
          histology.morphologie_icd_o && histology.morphologie_icd_o.length > 0 ||
          histology.lk_untersucht ||
          histology.lk_befallen ||
          histology.sentinel_lk_untersucht ||
          histology.sentinel_lk_befallen)
    },

    /**
     * Add new item as object to array
     */
    addNewHistology () {
      this.model = this.model || []

      if (Array.isArray(this.model)) {
        let newValue = {}

        if (this.leadDate) {
          newValue.tumor_histologiedatum = this.leadDate
          newValue.tumor_histologiedatum_genauigkeit = 'E'
        }

        if (this.diagnosis && (this.diagnosis === 'C61' || this.diagnosis.includes('C62'))) {
          newValue.grading = 'T'
        }

        this.model.unshift(newValue)
      }

      if (this.validationRef.fields[this.path]) {
        this.validationRef.fields[this.path].changed = true
      }
    },

    /**
     * Set label of grading required if histology date is set
     *
     * @param index The index of selected array element
     * @param text The text to localize
     * @returns {VueI18n.TranslateResult}
     */
    formatRequiredLabel (index, text) {
      let label = this.$t(text)

      // set * on selected element (e.g. grading or date)
      // date is required, if another element is filled
      // grading is required, if date is filled
      if (this.model[index].tumor_histologiedatum || this.checkIfDateIsRequired(this.model[index])) {
        label += ' *'
      }

      return label
    },

    /**
     * Validate date after changing date accuracy
     */
    validateDateAfterChangeOfAccuracy (index) {
      this.$nextTick(() => {
        if ( this.$refs[this.path + '.' + index + '.tumor_histologiedatum'] && this.$refs[this.path + '.' + index + '.tumor_histologiedatum'][0]) {
          this.$refs[this.path + '.' + index + '.tumor_histologiedatum'][0].validate()
        }
      })
    },

    /**
     * Delete morphology-code and description after changing version
     *
     * @param parentIndex The index of histology-element
     * @param index The index of morphology-element
     */
    resetMorphology (parentIndex, index) {
      this.$delete(this.model[parentIndex].morphologie_icd_o[index], 'code')
      this.$delete(this.model[parentIndex].morphologie_icd_o[index], 'description')
    },

    checkIfCodeHasCorrectSuffix (code) {
      const reg = new RegExp('/2|/3')
      return code && reg.test(code)
    },

    /**
     * Set date accuracy per default after date input
     *
     * @param index
     */
    setDateAccuracyAfterDate (index) {
      // set tumor_histologiedatum_genauigkeit if not exists after changing tumor_histologiedatum in datepicker
      if (this.model[index] && this.model[index].tumor_histologiedatum && !this.model[index].tumor_histologiedatum_genauigkeit) {
        this.model[index].tumor_histologiedatum_genauigkeit = 'E'
      } else if (this.model[index] && !this.model[index].tumor_histologiedatum) {
        this.$set(this.model[index], 'tumor_histologiedatum_genauigkeit', undefined)
      }
    },

    /**
     * Add new element to list of morphology-codes
     */
    addNewMorphologyCode (index) {
      this.$nextTick(() => {
        if (!this.model[index].morphologie_icd_o) {
          this.$set(this.model[index], 'morphologie_icd_o', [])
        }

        this.$set(this.model[index].morphologie_icd_o, this.model[index].morphologie_icd_o.length, {})
      })
    },

    /**
     * Delete selected element of histology and trigger validator for saving
     */
    deleteItem (path, index, field) {
      this.deleteSelectedItem(path, index)
      this.validationRef.fields[field].changed = true
    },

    /**
     * Get list of morphologie-codes codes via regex in auto-complete
     *
     * @param data The search string
     * @param index The index of selected item
     * @param parentIndex The index of selected histology
     */
    getDataForAutocomplete (data, index, parentIndex) {
      if (data && this.model[parentIndex].morphologie_icd_o[index].code && data !== this.model[parentIndex].morphologie_icd_o[index].code) {
        this.$set(this.model[parentIndex].morphologie_icd_o[index], 'description', undefined)
      }

      // clear string from special characters to provide errors
      // data = this.clearStringForRegexSearch(data)
      data = (data || '').replace(/#|\\|"|%|&/g, '')

      let config = {
        opts: {
          limit: 30
        },
        query: {
          searchValue: data
        }
      }

      // if icd-10 version is set, send it to backend
      const foundVersion = this.icdOVersions.find(elem => elem.value === this.model[parentIndex].morphologie_icd_o[index].version)

      if (foundVersion?.misdValue) {
        config.query.version = foundVersion.misdValue
      }

      this.$http.get('/morphology?config=' + JSON.stringify(config))
        .then(res => {
          this.resultSearchModel = {
            result: res.data.items,
            index: index,
            parentIndex: parentIndex,
            count: res.data.count || 0,
            version: res.data.version
          }
        })
        .catch(this.NotifyError)
    },

    /**
     * Set morphologie code value via list form auto complete
     *
     * @param data The selected element from list in auto-complete
     * @param index TThe index of selected morphology code
     * @param parentIndex The index of selected histology
     */
    setMorphology (data, index, parentIndex) {
      if (data && data.code) {
        const version = this.icdOVersionsFromLib[this.resultSearchModel.version] || this.formsMetaData.ICDO_Version

        this.$set(this.model[parentIndex].morphologie_icd_o[index], 'code', data.code)
        this.$set(this.model[parentIndex].morphologie_icd_o[index], 'description', data.description_long)
        this.$set(this.model[parentIndex].morphologie_icd_o[index], 'version', version)
      } else {
        if (!this.model[parentIndex].morphologie_icd_o[index].code || typeof data === 'string' || (this.validationRef.errors && this.validationRef.errors[this.path + '.' + parentIndex + '.morphologie_icd_o.' + index + '.code'] && this.validationRef.errors[this.path + '.' + parentIndex + '.morphologie_icd_o.' + index + '.code'].length > 0)) {
          // nextTick is necessary to trigger watcher in autocomplete on the right way
          this.$nextTick(() => {
            // delete input of morphology-code, if not selected from result list
            if (typeof data === 'string') this.$set(this.model[parentIndex].morphologie_icd_o[index], 'code', null)
          })

          this.$set(this.model[parentIndex].morphologie_icd_o[index], 'description', undefined)
        }
      }
    },

    /**
     * Set empty String-value of input with integer-typ to null
     *
     * @param value The value of Field
     * @param objectPath The path from "this.model" on
     * @param property The field name
     */
    refactorEmptyIntValueInArray (value, objectPath, property) {
      if (value === '') {
        this.$set(this.model[objectPath], property, null)
      }
    }
  },
  computed: {
    deathDate () {
      return this.patientId ? this.$store.getters.diagnosisBaseData(this.patientId).deathDate : undefined
    }
  },
  watch: {
    model: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$emit('input', newValue)
        }
      },
      immediate: true
    },
    value: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.model = newValue
        }
      },
      immediate: true
    },
    diagnosis: {
      handler: function (newValue) {
        // set grading of each element only after changing diagnosis and form is not disabled
        if (!this.isDisabled) {
          if (newValue && (newValue === 'C61' || newValue.includes('C62'))) {
            // iterate trough histology-array to set grading if necessary
            (this.model || []).forEach((histology, index) => {
              // set grading on "T" if in existing histology no grading exists or grading has a different value
              if (!histology.grading || histology.grading && histology.grading !== 'T') {
                this.$set(this.model[index], 'grading', 'T')
              }
            })
          } else {
            (this.model || []).forEach((histology, index) => {
              // set grading on "T" if in existing histology no grading exists or grading has a different value
              if (histology.grading && histology.grading === 'T') {
                this.$set(this.model[index], 'grading', undefined)
              }
            })
          }
        }
      }
    }
  }
}
</script>
